import { ReactNode, useDeferredValue, useState } from 'react'

import { AstraSegmentedControl } from '~/astra'
import { SearchText } from '~/components'
import { useSessionLikeSearchParam } from '~/hooks/useSessionLikeSearchParam'
import { useAllLocationsForHospitalConfig, useOperatingTheaterDipsResources } from '~/storm'

import { Section } from '../Section'
import { useResourceNavigation } from '../useResourceNavigation'
import { EditLocation } from './Form/EditLocation'
import { NewLocation } from './Form/NewLocation'
import { DipsRoomsTable, LocationsTable } from './RoomsTable'

const roomTabs = ['aktive', 'fromDips'] as const
type RoomTab = (typeof roomTabs)[number]
const tabOptions: { value: RoomTab; label: string }[] = [
    { value: 'aktive', label: 'Aktive' },
    { value: 'fromDips', label: 'Hentet fra DIPS' },
]

const isRoomTab = (value: string | undefined | null): value is RoomTab => !!value && roomTabs.includes(value as RoomTab)

const getRoomTab = (value: string | null): RoomTab => {
    if (isRoomTab(value)) return value
    return roomTabs[0]
}

export const RoomTables = () => {
    const [activeTab, setActiveTab] = useSessionLikeSearchParam('roomTab', getRoomTab)
    const [searchNeedle, setSearchNeedle] = useState('')

    const searchValue = useDeferredValue(searchNeedle)
    const simpleMatch = (value: string | null) =>
        value && searchValue.length > 0 ? value.trim().toLowerCase().includes(searchValue.trim().toLowerCase()) : true

    const allLocations = useAllLocationsForHospitalConfig()
    const dipsRooms = useOperatingTheaterDipsResources()

    const roomCodeToLocation = new Map(allLocations.map(l => [l.room_code, l]))
    const dipsResourceRows = dipsRooms
        .map(r => ({
            ...r,
            location: roomCodeToLocation.get(r.shortName ?? '') ?? null,
        }))
        .filter(r => !r.location && (simpleMatch(r.shortName) || simpleMatch(r.name)))

    const tableByTab: Record<RoomTab, { label: string; content: ReactNode }> = {
        aktive: {
            label: 'Aktive stuer',
            content: <LocationsTable locations={allLocations.filter(l => simpleMatch(l.room_code))} />,
        },
        fromDips: {
            label: 'Stuer hentet fra DIPS',
            content: <DipsRoomsTable resources={dipsResourceRows} />,
        },
    }

    return (
        <div className="flex h-full w-full flex-col gap-4 overflow-auto">
            <div>
                <AstraSegmentedControl<RoomTab>
                    value={activeTab}
                    onValueChange={value => setActiveTab(value as RoomTab)}
                    size="medium"
                    controlOptions={tabOptions}
                />
            </div>
            <Section
                title={tableByTab[activeTab].label}
                afterTitle={
                    <div className="ml-auto">
                        <SearchText rawNeedle={searchNeedle} setNeedle={setSearchNeedle} placeholder="Søk etter stuer" />
                    </div>
                }
            >
                {tableByTab[activeTab].content}
            </Section>
        </div>
    )
}

export const Rooms = () => {
    const { entityId: locationId, dipsResourceId } = useResourceNavigation()

    if (locationId !== null) {
        return <EditLocation />
    }
    if (dipsResourceId !== null) {
        return <NewLocation />
    }
    return <RoomTables />
}
